/**
 * Pure helpers behind the knitting kiosk screen.
 *
 * The page keeps all API calls and device state; the grouping of the work
 * queue, the colour/size matrix layout and the quantity guard live here so they
 * can be unit tested without a browser.
 */
import { lotColorNames } from './lotColor'
import type { BasketListItem, WorkQueueItem } from './shopfloor'

/** Localised fallbacks so this module stays free of vue-i18n. */
export interface KioskLabels {
  noColour: string
  noSize: string
}

/** One production order as shown on the product picker (name only, no MO code). */
export interface KioskProductGroup {
  orderId: number
  productName: string
  subtitle: string
  colors: string[]
  /** Total remaining quantity of all variants of the order. */
  remaining: number
  /** Distinct colour/size cells the operator will fill in (the matrix size). */
  cells: number
  rows: WorkQueueItem[]
}

export interface KioskMatrixCell {
  color: string
  /** Empty when this colour/size combination is not planned for the order. */
  workItemId: string
  remaining: number
}

export interface KioskMatrixRow {
  size: string
  cells: KioskMatrixCell[]
}

export interface KioskMatrix {
  colors: string[]
  rows: KioskMatrixRow[]
}

export function kioskColourLabel(row: WorkQueueItem, labels: KioskLabels): string {
  const explicit = (row.color_name ?? '').trim()
  if (explicit) return explicit
  // A lot-level summary can be an array of `{color_name, size_label}` objects;
  // taking `Object.keys()` of that array used to yield "0".
  const names = lotColorNames(row.color_summary)
  if (names.length) return names.join(' / ')
  return labels.noColour
}

export function kioskSizeLabel(row: WorkQueueItem, labels: KioskLabels): string {
  return (row.size_label ?? '').trim() || labels.noSize
}

/**
 * Remaining quantity of one order, counted per variant.
 *
 * A `variant` row already carries the remaining of the whole variant (plan minus
 * everything knitted into its lots), so the lot/basket rows of that same variant
 * must not be added on top — doing so showed an inflated total on the product
 * card. Lot/basket rows of a variant WITHOUT its own variant row are separate
 * work items (e.g. two lots at a downstream stage) and are counted individually.
 */
export function kioskRemainingTotal(rows: WorkQueueItem[]): number {
  const byVariant = new Map<number, number>()
  const rest: WorkQueueItem[] = []

  for (const row of rows) {
    const variantId = Number(row.production_order_variant_id)
    const isVariantRow = row.work_item_type === 'variant'
    if (isVariantRow && Number.isFinite(variantId) && variantId > 0) {
      byVariant.set(variantId, (byVariant.get(variantId) ?? 0) + (Number(row.remaining_quantity) || 0))
      continue
    }
    rest.push(row)
  }

  let total = 0
  for (const value of byVariant.values()) total += value
  for (const row of rest) {
    const variantId = Number(row.production_order_variant_id)
    if (Number.isFinite(variantId) && variantId > 0 && byVariant.has(variantId)) continue
    total += Number(row.remaining_quantity) || 0
  }
  return total
}

/** Distinct colour/size cells of an order (the size of its input matrix). */
export function kioskCellCount(rows: WorkQueueItem[], labels: KioskLabels): number {
  const keys = new Set<string>()
  for (const row of rows) keys.add(matrixKey(kioskSizeLabel(row, labels), kioskColourLabel(row, labels)))
  return keys.size
}

/**
 * Group the flat work queue into one card per production order. The MO code is
 * deliberately not carried into the group — the floor only needs the product.
 */
export function groupWorkQueue(rows: WorkQueueItem[], labels: KioskLabels): KioskProductGroup[] {
  const groups = new Map<number, KioskProductGroup>()
  for (const row of rows) {
    let group = groups.get(row.production_order_id)
    if (!group) {
      group = {
        orderId: row.production_order_id,
        productName: row.product_name || row.product_sku || String(row.product_id),
        subtitle: row.department_name ?? '',
        colors: [],
        remaining: 0,
        cells: 0,
        rows: [],
      }
      groups.set(row.production_order_id, group)
    }
    group.rows.push(row)
    const colour = kioskColourLabel(row, labels)
    if (!group.colors.includes(colour)) group.colors.push(colour)
  }
  for (const group of groups.values()) {
    group.remaining = kioskRemainingTotal(group.rows)
    group.cells = kioskCellCount(group.rows, labels)
  }
  return [...groups.values()].sort((left, right) => left.productName.localeCompare(right.productName))
}

function matrixKey(size: string, colour: string): string {
  return `${size}\u0000${colour}`
}

/**
 * Build the colour/size matrix of one order. Sizes are the rows (natural sort),
 * colours the columns; a cell without a work item is rendered as a void cell.
 */
export function buildKioskMatrix(rows: WorkQueueItem[], labels: KioskLabels): KioskMatrix {
  const lookup = new Map<string, WorkQueueItem>()
  const sizes: string[] = []
  const colors: string[] = []

  for (const row of rows) {
    const size = kioskSizeLabel(row, labels)
    const colour = kioskColourLabel(row, labels)
    if (!sizes.includes(size)) sizes.push(size)
    if (!colors.includes(colour)) colors.push(colour)
    const key = matrixKey(size, colour)
    const existing = lookup.get(key)
    // A variant row carries the remaining of the WHOLE variant, so it must win
    // over a lot/basket row of the same cell (same order as `kioskRemainingTotal`).
    if (!existing || (row.work_item_type === 'variant' && existing.work_item_type !== 'variant')) {
      lookup.set(key, row)
    }
  }

  sizes.sort((left, right) => left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' }))

  return {
    colors,
    rows: sizes.map((size) => ({
      size,
      cells: colors.map((colour) => {
        const item = lookup.get(matrixKey(size, colour))
        return {
          color: colour,
          workItemId: item ? item.work_item_id : '',
          remaining: item ? Number(item.remaining_quantity) || 0 : 0,
        }
      }),
    })),
  }
}

/**
 * Sanitise a raw matrix input value. Values above the remaining plan are clamped
 * so the operator cannot post an over-plan quantity the backend would reject.
 */
export function clampKioskQuantity(raw: string, maximum: number): { value: string; clamped: boolean } {
  const cleaned = raw.replace(/[^\d.]/g, '')
  if (cleaned === '') return { value: '', clamped: false }
  const parsed = Number(cleaned)
  if (!Number.isFinite(parsed)) return { value: '', clamped: false }
  if (parsed > maximum) return { value: String(maximum), clamped: true }
  return { value: cleaned, clamped: false }
}

/** Logout countdown the terminal uses until an operator configures its own. */
export const KIOSK_IDLE_TIMEOUT_S = 15
/** Shortest accepted countdown: a shorter one would log the worker out mid-step. */
export const KIOSK_IDLE_TIMEOUT_MIN_S = 5
/** Longest accepted countdown (one hour) — a forgotten card must still expire. */
export const KIOSK_IDLE_TIMEOUT_MAX_S = 3600

/**
 * Clamp the configured logout countdown (seconds) into the supported range.
 * Empty/invalid input falls back to the default, so a corrupted local config can
 * never leave the terminal signed in forever (or log the worker out instantly).
 */
export function clampKioskIdleTimeout(value: unknown): number {
  if (value === null || value === undefined || value === '') return KIOSK_IDLE_TIMEOUT_S
  const seconds = Math.round(Number(value))
  if (!Number.isFinite(seconds)) return KIOSK_IDLE_TIMEOUT_S
  return Math.min(KIOSK_IDLE_TIMEOUT_MAX_S, Math.max(KIOSK_IDLE_TIMEOUT_MIN_S, seconds))
}

/** Milliseconds of inactivity before the terminal runs its logout countdown. */
export function kioskIdleTimeoutMs(value: unknown): number {
  return clampKioskIdleTimeout(value) * 1000
}

/**
 * Keys of the on-screen number pad. `back` removes the last character, `clear`
 * empties the field.
 */
export type KioskNumpadKey = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '.' | 'back' | 'clear'

/** Number pad key rows (3 columns, `⌫` last). */
export const KIOSK_NUMPAD_ROWS: KioskNumpadKey[][] = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['.', '0', 'back'],
]

/**
 * Apply one number-pad key to a quantity field. Digits are appended and the
 * result is clamped to the remaining quantity, so a touch entry can never post
 * more than the plan (identical to typing into the input directly).
 */
export function kioskNumpadInput(
  current: string,
  key: KioskNumpadKey,
  maximum: number,
): { value: string; clamped: boolean } {
  const text = String(current ?? '')
  if (key === 'clear') return { value: '', clamped: false }
  if (key === 'back') return { value: text.slice(0, -1), clamped: false }
  if (key === '.') {
    if (text.includes('.')) return { value: text, clamped: false }
    return { value: text === '' ? '0.' : `${text}.`, clamped: false }
  }
  return clampKioskQuantity(`${text}${key}`, maximum)
}

/** Sum of the matrix entries the operator actually filled in. */
export function kioskEntries(
  rows: WorkQueueItem[],
  quantities: Record<string, string>,
  labels: KioskLabels,
): Array<{ row: WorkQueueItem; colour: string; size: string; quantity: number }> {
  return rows
    .map((row) => ({
      row,
      colour: kioskColourLabel(row, labels),
      size: kioskSizeLabel(row, labels),
      quantity: Number(quantities[row.work_item_id] ?? ''),
    }))
    .filter((entry) => Number.isFinite(entry.quantity) && entry.quantity > 0)
}

/**
 * Card uid candidates for one scanned value.
 *
 * Readers disagree on zero padding: the same physical card is reported as
 * `0001138776` by one reader while the employee card is registered as
 * `1138776` (or the other way round). The exact value is always tried first; a
 * second request is only made when the first one reports "worker not found".
 */
export function kioskCardUidVariants(uid: string): string[] {
  const exact = uid.trim()
  if (exact === '') return []

  const unpadded = exact.replace(/^0+/, '')
  return unpadded !== '' && unpadded !== exact ? [exact, unpadded] : [exact]
}

// --- RFID / keyboard-wedge card scan -------------------------------------
//
// RFID and barcode readers act as keyboards: they type the code quickly and end
// with Enter. The capture is burst based, so a stray key press never accumulates
// into a fake card number.

/** Characters kept from one burst (the API caps a card uid at 120). */
export const RFID_MAX_LENGTH = 64
/** Longest gap between two keystrokes that still counts as one scan burst. */
export const RFID_BURST_GAP_MS = 200
/** Shortest payload treated as a card scan rather than a stray key. */
export const RFID_MIN_LENGTH = 2

export interface RfidCapture {
  buffer: string
  lastKeyAt: number
}

export function newRfidCapture(): RfidCapture {
  return { buffer: '', lastKeyAt: 0 }
}

/**
 * Feed one keystroke to the capture.
 *
 * `scanned` is set only when the burst ends with Enter and the payload is long
 * enough to be a card; the buffer is always cleared by Enter so a failed read
 * cannot be submitted again.
 */
export function rfidKey(
  capture: RfidCapture,
  key: string,
  pressedAt: number,
  options: { gapMs?: number; maxLength?: number; minLength?: number } = {},
): { capture: RfidCapture; scanned: string | null } {
  const gapMs = options.gapMs ?? RFID_BURST_GAP_MS
  const maxLength = options.maxLength ?? RFID_MAX_LENGTH
  const minLength = options.minLength ?? RFID_MIN_LENGTH

  const stale = pressedAt - capture.lastKeyAt > gapMs
  const buffer = stale ? '' : capture.buffer

  if (key === 'Enter') {
    const scanned = buffer.trim()
    return {
      capture: { buffer: '', lastKeyAt: pressedAt },
      scanned: scanned.length >= minLength ? scanned : null,
    }
  }

  // Shift/Tab/arrow/F-keys carry multi-character names — never part of a code.
  if (key.length !== 1) return { capture: { buffer, lastKeyAt: pressedAt }, scanned: null }

  return {
    capture: { buffer: (buffer + key).slice(-maxLength), lastKeyAt: pressedAt },
    scanned: null,
  }
}

// --- terminal journal (baskets saved in the last 24 hours) ---------------
//
// There is no basket LIST endpoint on the backend (only create / detail by id /
// close), so the terminal keeps its own short journal of what it saved. It is
// also the only thing that keeps working while the network is down.

/** One colour/size line of a saved basket. */
export interface JournalLine {
  color: string
  size: string
  quantity: number
  lotNumber: string | null
}

/** One basket saved from this terminal. */
export interface JournalEntry {
  basketCode: string
  basketId: number
  employeeId: number | null
  employeeName: string
  productName: string
  stageCode: string
  /** Basket status as reported by the API (`open`/`advanced`/`completed`). */
  status?: string
  /** Local ISO timestamp of the save; the 24 h window is measured from here. */
  savedAt: string
  totalQuantity: number
  lines: JournalLine[]
}

/** The journal only shows the last 24 hours. */
export const KIOSK_JOURNAL_WINDOW_MS = 24 * 60 * 60 * 1000

/**
 * Journal entries worth showing: saved inside the window, optionally limited to
 * one worker, newest first. Entries with an unusable timestamp are dropped.
 */
export function kioskRecentJournal(
  entries: JournalEntry[],
  options: { now: Date; employeeId?: number | null; windowMs?: number },
): JournalEntry[] {
  const windowMs = options.windowMs ?? KIOSK_JOURNAL_WINDOW_MS
  const from = options.now.getTime() - windowMs

  return entries
    .filter((entry) => {
      const savedAt = new Date(entry.savedAt).getTime()
      if (!Number.isFinite(savedAt) || savedAt < from) return false
      if (options.employeeId != null && entry.employeeId !== options.employeeId) return false
      return true
    })
    .sort((left, right) => new Date(right.savedAt).getTime() - new Date(left.savedAt).getTime())
}

/**
 * `production_baskets.created_at` arrives as a PHP datetime (`Y-m-d H:i:s`),
 * which Safari cannot parse as-is. Normalise it to ISO local time.
 */
export function kioskApiTimestamp(value: string | null | undefined): string {
  const raw = (value ?? '').trim()
  if (raw === '') return ''
  return /^\d{4}-\d{2}-\d{2} /.test(raw) ? raw.replace(' ', 'T') : raw
}

/** Map one server basket row into the journal view model. */
export function kioskJournalFromBasket(item: BasketListItem): JournalEntry {
  return {
    basketCode: item.basket_code,
    basketId: item.id,
    employeeId: item.employee_id,
    employeeName: item.employee_name ?? '',
    productName: item.product_name ?? '',
    stageCode: item.stage_code,
    status: item.status,
    savedAt: kioskApiTimestamp(item.created_at),
    totalQuantity: Number(item.completed_quantity) || 0,
    lines: (item.lines ?? []).map((line) => ({
      color: line.color_name ?? '—',
      size: line.size_label ?? '—',
      quantity: Number(line.completed_quantity) || 0,
      lotNumber: line.lot_number ?? null,
    })),
  }
}

/** Totals of the visible journal lines (for the summary row). */
export function kioskJournalTotals(entries: JournalEntry[]): { baskets: number; quantity: number } {
  return entries.reduce(
    (totals, entry) => ({
      baskets: totals.baskets + 1,
      quantity: totals.quantity + (Number(entry.totalQuantity) || 0),
    }),
    { baskets: 0, quantity: 0 },
  )
}

/** Compact colour/size summary of one basket, for its single journal row. */
export function kioskJournalLineSummary(entry: JournalEntry): string {
  return entry.lines.map((line) => `${line.color} · ${line.size} ×${line.quantity}`).join(', ')
}

/** Lot numbers of one basket, used as the row's tooltip. */
export function kioskJournalLineLots(entry: JournalEntry): string {
  return entry.lines
    .map((line) => line.lotNumber)
    .filter((lot): lot is string => typeof lot === 'string' && lot !== '')
    .join(', ')
}

// --- paging --------------------------------------------------------------

export interface KioskPage<T> {
  items: T[]
  /** Requested page, clamped to the available range. */
  page: number
  pageCount: number
  total: number
}

/**
 * Slice a list for a pager, clamping an out-of-range page.
 *
 * Clamping matters because the journal is filtered client-side: changing the
 * filter or the 24 h window can shrink the list under the current page.
 */
export function kioskPaginate<T>(items: T[], page: number, perPage: number): KioskPage<T> {
  const size = Math.max(1, Math.floor(perPage) || 1)
  const total = items.length
  const pageCount = Math.max(1, Math.ceil(total / size))
  const requested = Math.floor(page) || 1
  const current = Math.min(Math.max(1, requested), pageCount)
  const start = (current - 1) * size

  return { items: items.slice(start, start + size), page: current, pageCount, total }
}

// --- terminal header (date + clock) --------------------------------------

/** `HH:MM:SS` for the terminal clock. Seconds are shown so the floor can time a basket. */
export function kioskClockLabel(date: Date): string {
  const pad = (value: number): string => String(value).padStart(2, '0')
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

const KIOSK_DATE_LOCALES: Record<string, string> = {
  en: 'en-GB',
  uz: 'uz-UZ',
  ru: 'ru-RU',
}

/** Long, locale-aware date line for the terminal header (e.g. "Friday, 16 October 2026"). */
export function kioskDateLabel(date: Date, locale: string): string {
  const tag = KIOSK_DATE_LOCALES[locale] ?? locale ?? 'en-GB'
  try {
    return new Intl.DateTimeFormat(tag, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date)
  } catch {
    const pad = (value: number): string => String(value).padStart(2, '0')
    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`
  }
}

// --- stage code discovery -------------------------------------------------
//
// There is no global "knitting" stage code: every production order carries the
// stage codes of its own BOM (`production_order_stages.stage_code`). A first
// stage terminal must therefore read the codes from the open orders instead of
// hardcoding one, otherwise `work-queue?stage_code=knitting` returns nothing.

/** Minimal shape of `GET /production-orders/{id}/stages` used for discovery. */
export interface StageLike {
  stage_code: string
  operation_sequence: number | string
}

/** Stage codes that are the FIRST operation (lowest sequence) of one order. */
export function firstOperationStageCodes(stages: StageLike[]): string[] {
  const normalised = stages
    .map((stage) => ({
      code: (stage.stage_code ?? '').trim(),
      sequence: Number(stage.operation_sequence) || 0,
    }))
    .filter((stage) => stage.code !== '')

  if (!normalised.length) return []

  const minimum = Math.min(...normalised.map((stage) => stage.sequence))
  const codes: string[] = []
  for (const stage of normalised) {
    if (stage.sequence === minimum && !codes.includes(stage.code)) codes.push(stage.code)
  }
  return codes
}

/** Parse the terminal's manual stage-code list: "knitting, op_10" → list. */
export function parseStageCodes(input: string): string[] {
  return Array.from(
    new Set(
      input
        .split(',')
        .map((code) => code.trim().toLowerCase())
        .filter((code) => code !== ''),
    ),
  )
}

/** Merge the per-stage queue pages, de-duplicating by `work_item_id`. */
export function mergeWorkQueue<T extends { work_item_id: string }>(lists: T[][]): T[] {
  const byId = new Map<string, T>()
  for (const list of lists) {
    for (const item of list) {
      if (!byId.has(item.work_item_id)) byId.set(item.work_item_id, item)
    }
  }
  return [...byId.values()]
}

/**
 * Keep only the rows whose stage is the first operation of their order, so a
 * knitting terminal never shows work that belongs to a downstream workshop.
 * Rows of an order whose stages could not be read are kept (never hide work
 * because discovery failed).
 */
export function firstStageOnlyRows<
  T extends { production_order_id: number; stage_code: string },
>(rows: T[], firstStageByOrder: Record<string, string[]>): T[] {
  return rows.filter((row) => {
    const allowed = firstStageByOrder[String(row.production_order_id)]
    if (!allowed || !allowed.length) return true
    return allowed.includes(row.stage_code)
  })
}

